import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, Loader2 } from 'lucide-react';
import blogsApi, { CreateBlogData } from '../services/blogsApi';
import Toast from '../components/Toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BlogForm = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    const [formData, setFormData] = useState<CreateBlogData>({
        title: '',
        category: '',
        description: '',
        content: '',
        slug: '',
        author: '',
        readTime: '',
        image: '',
    });

    const [imagePreview, setImagePreview] = useState<string>('');

    useEffect(() => {
        if (id) {
            setIsEditing(true);
            loadBlog();
        }
    }, [id]);

    // Quill editor modules configuration
    const modules = useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image', 'code-block'],
            [{ 'color': [] }, { 'background': [] }],
            ['clean']
        ],
    }), []);

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'align',
        'link', 'image', 'code-block',
        'color', 'background'
    ];

    const loadBlog = async () => {
        if (!id) return;

        try {
            setLoading(true);
            const blog = await blogsApi.getById(id);
            setFormData({
                title: blog.title,
                category: blog.category,
                description: blog.description,
                content: blog.content,
                slug: blog.slug,
                author: blog.author || '',
                readTime: blog.readTime || '',
                image: blog.image || '',
            });
            if (blog.image) {
                setImagePreview(blog.image);
            }
        } catch (error: any) {
            setToast({ message: error.message || 'Failed to load blog', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Auto-generate slug from title
        if (name === 'title' && !isEditing) {
            const slug = value
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '');
            setFormData(prev => ({ ...prev, slug }));
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            // Set local preview immediately
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);

            // Upload to S3 immediately
            setUploading(true);
            const response = await blogsApi.uploadImage(file);

            // Set the presigned URL in the form data
            setFormData(prev => ({ ...prev, image: response.presignedUrl }));
            setToast({ message: 'Image uploaded successfully', type: 'success' });
        } catch (error: any) {
            setToast({ message: `Image upload failed: ${error.message}`, type: 'error' });
            // Clear the preview on error
            setImagePreview('');
            const fileInput = document.getElementById('blog-image-input') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setLoading(true);

            // Image is already uploaded in handleFileSelect, just use formData as-is
            const blogData = {
                ...formData,
            };

            if (isEditing && id) {
                await blogsApi.update(id, blogData);
                setToast({ message: 'Blog updated successfully, redirecting to list...', type: 'success' });
            } else {
                await blogsApi.create(blogData);
                setToast({ message: 'Blog created successfully, redirecting to list...', type: 'success' });
            }

            setTimeout(() => navigate('/blogs'), 1500);
        } catch (error: any) {
            setToast({ message: error.message || 'Failed to save blog', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEditing) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/blogs')}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {isEditing ? 'Edit Blog' : 'Create Blog'}
                        </h1>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {isEditing ? 'Update your blog post' : 'Write a new blog post'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Image Upload */}
                <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Cover Image
                    </label>
                    {imagePreview ? (
                        <div className="relative">
                            <img
                                src={imagePreview}
                                alt="Preview"
                                className="w-full h-64 object-cover rounded-lg"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setImagePreview('');
                                    setFormData(prev => ({ ...prev, image: '' }));
                                    // Clear the file input
                                    const fileInput = document.getElementById('blog-image-input') as HTMLInputElement;
                                    if (fileInput) fileInput.value = '';
                                }}
                                className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ) : (
                        <label className="relative block border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors">
                            {uploading ? (
                                <>
                                    <Loader2 className="mx-auto h-12 w-12 text-primary-600 animate-spin" />
                                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                        Uploading image...
                                    </p>
                                </>
                            ) : (
                                <>
                                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                        Click to upload or drag and drop
                                    </p>
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                                        PNG, JPG, GIF up to 5MB
                                    </p>
                                </>
                            )}
                            <input
                                id="blog-image-input"
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                disabled={uploading}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                            />
                        </label>
                    )}
                </div>

                {/* Blog Details */}
                <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                style={{ textTransform: 'none' }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Category *
                            </label>
                            <input
                                type="text"
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                required
                                placeholder="e.g., Security, Technology"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                style={{ textTransform: 'none' }}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Slug *
                        </label>
                        <input
                            type="text"
                            name="slug"
                            value={formData.slug}
                            onChange={handleInputChange}
                            required
                            placeholder="blog-post-url-slug"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            URL-friendly version of the title (auto-generated)
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Description *
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            required
                            rows={3}
                            placeholder="Brief description of the blog post"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            style={{ textTransform: 'none' }}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Author
                            </label>
                            <input
                                type="text"
                                name="author"
                                value={formData.author}
                                onChange={handleInputChange}
                                placeholder="John Doe"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                style={{ textTransform: 'none' }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Read Time
                            </label>
                            <input
                                type="text"
                                name="readTime"
                                value={formData.readTime}
                                onChange={handleInputChange}
                                placeholder="5 min"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                style={{ textTransform: 'none' }}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Content *
                        </label>
                        <div className="prose-editor" style={{ textTransform: 'none' }}>
                            <ReactQuill
                                theme="snow"
                                value={formData.content}
                                onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                                modules={modules}
                                formats={formats}
                                placeholder="Write your blog content here..."
                                className="bg-white dark:bg-gray-700 rounded-md"
                                style={{ height: '400px', marginBottom: '42px' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-4">
                    <button
                        type="button"
                        onClick={() => navigate('/blogs')}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || uploading}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {(loading || uploading) && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
                        {uploading ? 'Uploading...' : loading ? 'Saving...' : isEditing ? 'Update Blog' : 'Create Blog'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BlogForm;
